import React, { useEffect, useRef, useState } from 'react';

import { Box, Typography } from '@mui/joy';

import { commandStore } from '../../stores/CommandStore';
import { CameraZoomToCommand, GimbalMoveToCommand } from '../../types/DroneCommands/CommandTypes';
import AntMediaVideo, { AntMediaVideoProps } from './AntMediaVideo';

export interface ControlledAntMediaVideoProps extends AntMediaVideoProps {
  /** Horizontal field-of-view of the camera in degrees */
  horizontalFov?: number;
  /** Vertical field-of-view of the camera in degrees */
  verticalFov?: number;
  /** Minimum zoom factor */
  minZoom?: number;
  /** Maximum zoom factor */
  maxZoom?: number;
}

const DEFAULT_HORIZONTAL_FOV = 81; // deg
const DEFAULT_VERTICAL_FOV = 51.32; // deg

/**
 * A thin wrapper around <AntMediaVideo/> that adds gimbal click-to-point control
 * and scroll-wheel zoom. All streaming logic stays inside AntMediaVideo; this
 * component only overlays an invisible div that captures mouse events and
 * sends commands via commandStore.
 */
const ControlledAntMediaVideo: React.FC<ControlledAntMediaVideoProps> = ({
  horizontalFov = DEFAULT_HORIZONTAL_FOV,
  verticalFov = DEFAULT_VERTICAL_FOV,
  minZoom = 1.0,
  maxZoom = 6.0,
  ...videoProps
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [zoom, setZoom] = useState(1.0);

  /* ----------------------------- helpers ----------------------------- */
  const sendGimbalCommand = (pitchDeg: number, yawDeg: number) => {
    const command: GimbalMoveToCommand = {
      command_type: 'gimbal_move_to',
      version: '1.0',
      payload: {
        pitch_deg: pitchDeg,
        yaw_deg: yawDeg,
        relative: false
      }
    };
    commandStore.sendCommand(command);
  };

  const sendZoomCommand = (newZoom: number) => {
    const command: CameraZoomToCommand = {
      command_type: 'camera_zoom_to',
      version: '1.0',
      payload: {
        zoom_level: newZoom
      }
    };
    commandStore.sendCommand(command);
  };

  /* ---------------------------- event handlers ---------------------------- */
  const handlePointerEvent = (clientX: number, clientY: number) => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    // find the <video> element rendered by AntMediaVideo so we can obtain its intrinsic size
    const video = wrapper.querySelector('video') as HTMLVideoElement | null;
    if (!video) return;

    const { width, height, top, left } = wrapper.getBoundingClientRect();
    const videoAspectRatio = video.videoWidth && video.videoHeight ? video.videoWidth / video.videoHeight : 16 / 9;
    const containerAspectRatio = width / height;

    let effectiveWidth: number;
    let effectiveHeight: number;
    let offsetX: number;
    let offsetY: number;

    if (containerAspectRatio > videoAspectRatio) {
      // black bars on left/right
      effectiveHeight = height;
      effectiveWidth = height * videoAspectRatio;
      offsetX = (width - effectiveWidth) / 2;
      offsetY = 0;
    } else {
      // black bars on top/bottom
      effectiveWidth = width;
      effectiveHeight = width / videoAspectRatio;
      offsetX = 0;
      offsetY = (height - effectiveHeight) / 2;
    }

    const effectiveX = clientX - left - offsetX;
    const effectiveY = clientY - top - offsetY;
    if (effectiveX < 0 || effectiveX > effectiveWidth || effectiveY < 0 || effectiveY > effectiveHeight) {
      return; // click outside video frame
    }

    const normalizedX = (effectiveX / effectiveWidth) * 2 - 1; // -1 .. 1
    const normalizedY = (effectiveY / effectiveHeight) * 2 - 1; // -1 .. 1 (positive is down)

    const yaw = normalizedX * (horizontalFov / 2);
    const pitch = -normalizedY * (verticalFov / 2);

    sendGimbalCommand(pitch, yaw);
  };

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    handlePointerEvent(e.clientX, e.clientY);
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const video = wrapper.querySelector('video') as HTMLVideoElement | null;
    if (!video) return;

    const { width, height, top, left } = wrapper.getBoundingClientRect();
    const videoAspectRatio = video.videoWidth && video.videoHeight ? video.videoWidth / video.videoHeight : 16 / 9;
    const containerAspectRatio = width / height;

    let effectiveWidth: number;
    let effectiveHeight: number;
    let offsetX: number;
    let offsetY: number;

    if (containerAspectRatio > videoAspectRatio) {
      effectiveHeight = height;
      effectiveWidth = height * videoAspectRatio;
      offsetX = (width - effectiveWidth) / 2;
      offsetY = 0;
    } else {
      effectiveWidth = width;
      effectiveHeight = width / videoAspectRatio;
      offsetX = 0;
      offsetY = (height - effectiveHeight) / 2;
    }

    const effectiveX = e.clientX - left - offsetX;
    const effectiveY = e.clientY - top - offsetY;
    if (effectiveX >= 0 && effectiveX <= effectiveWidth && effectiveY >= 0 && effectiveY <= effectiveHeight) {
      wrapper.style.cursor = 'crosshair';
    } else {
      wrapper.style.cursor = 'default';
    }
  };

  const handleWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    const STEP = 0.1;
    let newZoom = zoom;
    if (e.deltaY < 0) {
      newZoom = Math.max(minZoom, zoom - STEP);
    } else {
      newZoom = Math.min(maxZoom, zoom + STEP);
    }
    if (newZoom !== zoom) {
      setZoom(newZoom);
      sendZoomCommand(newZoom);
    }
    e.preventDefault();
    e.stopPropagation();
  };

  /* ----------------------------- render ----------------------------- */
  return (
    <Box ref={wrapperRef} position='relative' width='100%' height='100%'>
      <AntMediaVideo {...videoProps} />
      {/* overlay for interaction */}
      <Box
        position='absolute'
        top={0}
        right={0}
        bottom={0}
        left={0}
        onClick={handleClick}
        onMouseMove={handleMouseMove}
        onWheel={handleWheel}
        sx={{ cursor: 'crosshair' }}
      />
      {/* zoom overlay label */}
      <Typography position='absolute' top={8} left={8} level='body-md'>
        {zoom.toFixed(1)}x
      </Typography>
    </Box>
  );
};

export default ControlledAntMediaVideo;
